import type { Response } from '@/api/auth/types';
import type { DevToolsAccess } from '@/api/dev';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { getApiClientId } from '@/api/knowledge/helpers';
import {
  PLATFORM_NAV_ITEMS,
  type PlatformModule,
} from '@/constants/platformNav';
import { get } from '@/utils/request';
import { useUserStore } from './user';

const emptyAccess: DevToolsAccess = {
  assistant: false,
  experts: false,
  apps: false,
  automation: false,
  resources: false,
  datahub: false,
  studio: false,
  datahubPath: '/datahub',
  studioPath: '/studio-app',
};

function navFlagsFromPerms(
  set: Set<string>,
  all: boolean,
): Pick<DevToolsAccess, 'assistant' | 'experts' | 'apps' | 'automation' | 'resources'> {
  return {
    assistant: all || set.has('chat:nav:assistant'),
    experts: all || set.has('chat:nav:experts'),
    apps: all || set.has('chat:nav:apps'),
    automation: all || set.has('chat:nav:automation'),
    resources: all || set.has('chat:nav:resources'),
  };
}

function normalizeAccessFromPerms(perms: Iterable<string> | undefined | null): DevToolsAccess | null {
  if (!perms)
    return null;
  const set = new Set(Array.from(perms).filter(Boolean));
  if (set.size === 0)
    return null;

  const all = set.has('*:*:*');
  const hasModule = all || set.has('chat:dev');

  return {
    ...navFlagsFromPerms(set, all),
    datahub: hasModule,
    studio: hasModule,
    datahubPath: '/datahub',
    studioPath: '/studio-app',
  };
}

async function loadPermsFallback(): Promise<DevToolsAccess | null> {
  const userStore = useUserStore();
  const fromStore = normalizeAccessFromPerms(userStore.userInfo?.menuPermission);
  if (fromStore)
    return fromStore;

  try {
    const res = await get<Response & { permissions?: string[] }>('/system/user/getInfo').json();
    if (res && (res.code === 200 || res.code === 0)) {
      const perms = res.permissions
        || (res.data as any)?.permissions
        || userStore.userInfo?.menuPermission;
      if (perms && userStore.userInfo) {
        userStore.setUserInfo({ ...userStore.userInfo, menuPermission: Array.from(perms) });
      }
      return normalizeAccessFromPerms(perms);
    }
  }
  catch {
    // ignore
  }
  return null;
}

/** 现网可能没有该接口；用原生 fetch，避免 hook-fetch 对 500 弹全局错误。 */
async function tryLoadDevToolsApi(): Promise<DevToolsAccess | null> {
  try {
    const userStore = useUserStore();
    const base = String(import.meta.env.VITE_API_URL || '').replace(/\/+$/, '');
    const clientId = getApiClientId();
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    if (userStore.token)
      headers.Authorization = `Bearer ${userStore.token}`;
    if (clientId)
      headers.clientId = clientId;
    const res = await fetch(`${base}/chat/dev-tools/access`, { headers });
    if (!res.ok)
      return null;
    const json = await res.json();
    if ((json?.code === 200 || json?.code === 0) && json.data)
      return json.data as DevToolsAccess;
  }
  catch {
    // ignore
  }
  return null;
}

export const useDevToolsStore = defineStore('devTools', () => {
  const access = ref<DevToolsAccess>({ ...emptyAccess });
  const loaded = ref(false);
  let inflight: Promise<void> | null = null;

  const canSeeDev = computed(() => access.value.datahub || access.value.studio);

  function canSeeModule(key: PlatformModule): boolean {
    if (key === 'dev')
      return canSeeDev.value;
    return access.value[key] === true;
  }

  const navItems = computed(() =>
    PLATFORM_NAV_ITEMS.filter(item => canSeeModule(item.key)),
  );

  const hideSidebar = computed(() =>
    navItems.value.length === 1 && navItems.value[0]?.key === 'apps',
  );

  const homePath = computed(() => navItems.value[0]?.path || '/403');

  async function fetchAccess(force = false) {
    if (loaded.value && !force)
      return;
    if (inflight)
      return inflight;

    inflight = (async () => {
      const fromPerms = (await loadPermsFallback()) ?? { ...emptyAccess };
      const fromApi = await tryLoadDevToolsApi();
      access.value = {
        assistant: fromApi?.assistant ?? fromPerms.assistant,
        experts: fromApi?.experts ?? fromPerms.experts,
        apps: fromApi?.apps ?? fromPerms.apps,
        automation: fromApi?.automation ?? fromPerms.automation,
        resources: fromApi?.resources ?? fromPerms.resources,
        datahub: fromApi ? !!fromApi.datahub : fromPerms.datahub,
        studio: fromApi ? !!fromApi.studio : fromPerms.studio,
        datahubPath: fromApi?.datahubPath || fromPerms.datahubPath || '/datahub',
        studioPath: fromApi?.studioPath || fromPerms.studioPath || '/studio-app',
      };
      loaded.value = true;
      inflight = null;
    })();

    return inflight;
  }

  function reset() {
    access.value = { ...emptyAccess };
    loaded.value = false;
    inflight = null;
  }

  return {
    access,
    loaded,
    canSeeDev,
    canSeeModule,
    navItems,
    hideSidebar,
    homePath,
    fetchAccess,
    reset,
  };
});
